/**
 * 請求書の読み書き(2026-10-23 段階3)。計算は invoice.ts。
 * 書ける人: 経理・管理者(firestore.rules の invoices)。読める人: 経理・管理者・承認者
 *  - 番号の採番は counters/invoice-<会社略号>-<YYYYMM> をトランザクションで1つ進める(会社×月ごとの連番)
 *  - 入金の記録・取消はトランザクションで請求書を読み直してから書く(同時に2人が記録しても合計がずれない)
 *  - 売上の取引(transactions)は書き換えない。どの売上を請求したかは請求書の明細(lines.transaction_id)で持つ
 *  - 同じ売上を2枚の請求書に入れない: 発行のトランザクションで「請求済みの印」(invoice_links/<売上ID>)が無いことを確かめて作る。
 *    取消のときに印を消す(2人が同時に発行しても、計上日を直して別の月に出てきても、2枚目は止まる)
 */
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import { PL_QUERY_STATUSES, type CashEntry, type DateStr, type Invoice, type MonthStr, type Transaction } from '@core/lib/types';
import { invoiceCounterKey, invoiceNumberOf, statusOf, validateDraft, validateReceipt, type InvoiceDraft } from './invoice';

const COLLECTION = 'invoices';
const LINKS = 'invoice_links';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export async function fetchInvoicesForMonth(month: MonthStr): Promise<Invoice[]> {
  const snap = await getDocs(query(collection(getDb(), COLLECTION), where('period_month', '==', month)));
  return snap.docs.map((d) => withId<Invoice>(d)).sort((a, b) => (a.number < b.number ? -1 : 1));
}

/** 入金がまだ済んでいない請求書(未入金・一部入金) */
export async function fetchOpenInvoices(): Promise<Invoice[]> {
  const snap = await getDocs(query(collection(getDb(), COLLECTION), where('status', 'in', ['issued', 'partial'])));
  return snap.docs.map((d) => withId<Invoice>(d)).sort((a, b) => (a.due_date < b.due_date ? -1 : 1));
}

/** 対象月の売上の候補(承認前+承認済み以降。売上の科目かどうかは invoice.ts の unbilledSales で絞る) */
export async function fetchSalesForMonth(month: MonthStr): Promise<Transaction[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'transactions'), where('status', 'in', PL_QUERY_STATUSES), where('posting_month', '==', month)),
  );
  return snap.docs.map((d) => withId<Transaction>(d));
}

/** 発行する。番号は会社×月ごとの連番。戻り値は作った請求書のIDと番号 */
export async function createInvoice(params: {
  draft: InvoiceDraft;
  companyCode: string;
  userId: string;
}): Promise<{ id: string; number: string }> {
  const { draft, companyCode, userId } = params;
  const invalid = validateDraft(draft, companyCode);
  if (invalid) throw new Error(invalid);
  const db = getDb();
  const ref = doc(collection(db, COLLECTION));
  const counterRef = doc(db, 'counters', invoiceCounterKey(companyCode, draft.period_month));
  const txIds = draft.lines.map((l) => l.transaction_id).filter((x): x is string => !!x);
  const number = await runTransaction(db, async (tx) => {
    // 読み取りを先に済ませる(トランザクションの決まり)
    const snap = await tx.get(counterRef);
    const links = await Promise.all(txIds.map((id) => tx.get(doc(db, LINKS, id))));
    const billed = links.find((l) => l.exists());
    if (billed) {
      const line = draft.lines.find((l) => l.transaction_id === billed.id);
      throw new Error(
        `「${line?.description ?? billed.id}」の売上は、すでに請求書 ${String(billed.data()?.invoice_number ?? '')} に入っています(外してから発行してください)`,
      );
    }
    const next = ((snap.data()?.value as number | undefined) ?? 0) + 1;
    const no = invoiceNumberOf(companyCode, draft.period_month, next);
    tx.set(counterRef, { value: next }, { merge: true });
    for (const id of txIds) tx.set(doc(db, LINKS, id), { invoice_id: ref.id, invoice_number: no, created_by: userId, created_at: serverTimestamp() });
    tx.set(ref, {
      ...draft,
      number: no,
      created_by: userId,
      created_at: serverTimestamp(),
      updated_by: userId,
      updated_at: serverTimestamp(),
    });
    return no;
  });
  await writeAuditLog({
    targetType: 'master',
    targetId: ref.id,
    action: 'invoice-create',
    by: userId,
    after: { number, total: draft.total, customer_id: draft.customer_id, period_month: draft.period_month },
  });
  return { id: ref.id, number };
}

/**
 * 入金の照合に使う、ある日以降の入出金(銀行の入金を探す。絞り込みは receiptMatching.ts)。
 * 読めるのは「確定」の権限を持つ人(権限ルールの cash_entries)。日付だけで絞り、索引を増やさない
 */
export async function fetchCashEntriesSince(from: DateStr, max = 2000): Promise<CashEntry[]> {
  const snap = await getDocs(query(collection(getDb(), 'cash_entries'), where('date', '>=', from), orderBy('date'), limit(max)));
  return snap.docs.map((d) => withId<CashEntry>(d));
}

/** 入金予定日がある日以降の請求書(どの入金をもう使ったかを調べるため。状態は問わない) */
export async function fetchInvoicesDueSince(from: DateStr): Promise<Invoice[]> {
  const snap = await getDocs(query(collection(getDb(), COLLECTION), where('due_date', '>=', from)));
  return snap.docs.map((d) => withId<Invoice>(d));
}

/** 入金を記録する(返金はマイナスの金額)。銀行の入金から消し込むときは cashEntryId を渡す */
export async function recordReceipt(params: {
  invoiceId: string;
  date: DateStr;
  amount: number;
  note: string;
  userId: string;
  cashEntryId?: string | null;
}): Promise<Invoice['status']> {
  const { invoiceId, date, amount, note, userId } = params;
  const cashEntryId = params.cashEntryId ?? null;
  const db = getDb();
  const ref = doc(db, COLLECTION, invoiceId);
  const status = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('請求書が見つかりません');
    const inv = snap.data() as Invoice;
    const invalid = validateReceipt(inv, { date, amount });
    if (invalid) throw new Error(invalid);
    if (cashEntryId && inv.receipts.some((r) => r.cash_entry_id === cashEntryId)) throw new Error('この入金はすでに消し込まれています');
    const received = inv.received + amount;
    const next = statusOf(inv.total, received, inv.status);
    tx.update(ref, {
      receipts: [
        ...inv.receipts,
        {
          id: `r${Date.now()}`,
          date,
          amount,
          method: cashEntryId ? 'csv' : 'manual',
          cash_entry_id: cashEntryId,
          note: note.trim() || null,
          by: userId,
          at: new Date().toISOString(),
        },
      ],
      received,
      status: next,
      updated_by: userId,
      updated_at: serverTimestamp(),
    });
    return next;
  });
  await writeAuditLog({ targetType: 'master', targetId: invoiceId, action: 'invoice-receipt', by: userId, after: { date, amount, cash_entry_id: cashEntryId } });
  return status;
}

/** 取り消す(入金の記録があるものは取り消せない)。番号は欠番として残る */
export async function voidInvoice(params: { invoiceId: string; reason: string; userId: string }): Promise<void> {
  const { invoiceId, reason, userId } = params;
  if (!reason.trim()) throw new Error('取消の理由を入れてください');
  const db = getDb();
  const ref = doc(db, COLLECTION, invoiceId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('請求書が見つかりません');
    const inv = snap.data() as Invoice;
    if (inv.status === 'void') throw new Error('すでに取り消されています');
    if (inv.receipts.length > 0) throw new Error('入金の記録がある請求書は取り消せません');
    // 請求済みの印を消す(この請求書の印だけ。取り消した請求書の売上は、また請求できる)
    const ids = inv.lines.map((l) => l.transaction_id).filter((x): x is string => !!x);
    const links = await Promise.all(ids.map((id) => tx.get(doc(db, LINKS, id))));
    tx.update(ref, { status: 'void', void_reason: reason.trim(), updated_by: userId, updated_at: serverTimestamp() });
    for (const l of links) if (l.exists() && l.data()?.invoice_id === invoiceId) tx.delete(l.ref);
  });
  await writeAuditLog({ targetType: 'master', targetId: invoiceId, action: 'invoice-void', by: userId, after: { reason } });
}

/** 入金予定日を直す(発行日より前にはできない) */
export async function updateDueDate(params: { invoiceId: string; dueDate: DateStr; userId: string }): Promise<void> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(params.dueDate)) throw new Error('入金予定日を入れてください');
  const db = getDb();
  const ref = doc(db, COLLECTION, params.invoiceId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('請求書が見つかりません');
    const inv = snap.data() as Invoice;
    if (inv.status === 'void') throw new Error('取り消した請求書は直せません');
    if (params.dueDate < inv.issue_date) throw new Error('入金予定日が発行日より前です');
    tx.update(ref, { due_date: params.dueDate, updated_by: params.userId, updated_at: serverTimestamp() });
  });
  await writeAuditLog({ targetType: 'master', targetId: params.invoiceId, action: 'invoice-due-date', by: params.userId, after: { due_date: params.dueDate } });
}
